import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { resourceListService } from "../services/resourceListService";
import { useOrg } from "../contexts/OrganizationContext";
import { useBodyClass } from "../hooks/useBodyClass";
import { useContentTop } from "../hooks/useContentTop";
import MapContainer from "../components/Map/MapContainer";
import CartItem from "../components/Cart/CartItem";
import { parseLatLng } from "../utils/geo";

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validatePhoneNumber(phone) {
  return /^\d{10}$/.test(phone.replace(/\D/g, ""));
}

function ResourceListDetailPage({ shared }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { organization } = useOrg();

  const [list, setList] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selectedItemId, setSelectedItemId] = React.useState(null);
  // Set only when selection originates from a marker click, so hovering a list
  // item never triggers a scroll. Cleared once the target is scrolled into view.
  const [scrollTargetId, setScrollTargetId] = React.useState(null);
  const itemRefs = React.useRef({});

  // Share modal state
  const [shareModalOpen, setShareModalOpen] = React.useState(false);
  const [shareTab, setShareTab] = React.useState("email");
  const [shareEmail, setShareEmail] = React.useState("");
  const [sharePhone, setSharePhone] = React.useState("");
  const [shareClient, setShareClient] = React.useState("");
  const [shareComments, setShareComments] = React.useState("");
  const [shareReplyToMe, setShareReplyToMe] = React.useState(true);
  const [shareError, setShareError] = React.useState("");
  const [shareConfirmation, setShareConfirmation] = React.useState("");
  const [linkCopied, setLinkCopied] = React.useState(false);
  const [removingItemId, setRemovingItemId] = React.useState(null);

  const mapCenter = organization.serviceLocale?.geo?.mapCenter || { lat: 41.6, lng: -72.7 };
  const enableSMS = organization.flags?.enableSMS;
  const currentUser = window.UW211?.CURRENT_USER;

  useBodyClass(shared ? "page-sharedResourceList" : "page-resourceList");
  useContentTop();

  React.useEffect(() => {
    const contentRegion = document.getElementById("content-region");
    if (contentRegion) contentRegion.classList.add("split-content-region");
    return () => {
      if (contentRegion) contentRegion.classList.remove("split-content-region");
    };
  }, []);

  React.useEffect(() => {
    setLoading(true);
    resourceListService
      .get(id)
      .then((data) => {
        setList(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [id]);

  const items = list?.items || list?.resource_list_items || [];

  function handleRemoveItem(item) {
    const itemId = item.id || item.resource_list_item_id;
    if (!window.confirm("Remove this resource from the list?")) return;
    setRemovingItemId(itemId);
    resourceListService.removeItem(list?.hashid || id, itemId).then(() => {
      setList((prev) => {
        if (!prev) return prev;
        const key = prev.items ? "items" : "resource_list_items";
        return { ...prev, [key]: prev[key].filter((i) => (i.id || i.resource_list_item_id) !== itemId) };
      });
      setRemovingItemId(null);
    }).catch(() => {
      setRemovingItemId(null);
    });
  }

  const mapResults = React.useMemo(
    () =>
      items
        .map((item) => ({
          ...item,
          latLng: item.latLng || parseLatLng(item.location),
          addressLine: [item.address_1, item.address_2, item.city, item.state]
            .filter(Boolean)
            .join(", "),
        }))
        .filter((item) => item.latLng && item.latLng.lat && item.latLng.lng),
    [items]
  );

  function handleSelectItem(item) {
    setSelectedItemId(item?.program_site_id || item?.id);
  }

  function handleMarkerClick(item) {
    const itemId = item?.program_site_id || item?.id;
    setSelectedItemId(itemId);
    setScrollTargetId(itemId);
  }

  React.useEffect(() => {
    if (scrollTargetId == null) return;
    const node = itemRefs.current[scrollTargetId];
    if (node) node.scrollIntoView({ behavior: "smooth", block: "nearest" });
    setScrollTargetId(null);
  }, [scrollTargetId]);

  function handleNavigateToItem(item) {
    const resourceId = item.program_site_id || item.id;
    const base = shared ? `/shared/resource-lists/${id}` : `/my211/resource-lists/${id}`;
    navigate(`${base}/resource/${resourceId}`);
  }

  function handlePrint() {
    setShareModalOpen(false);
    setTimeout(() => {
      window.print();
    }, 100);
  }

  function handleShareOpen(e) {
    e.preventDefault();
    setShareModalOpen(true);
  }

  function handleShareClose() {
    setShareModalOpen(false);
    setShareError("");
  }

  function handleCopyLink() {
    const url = `${window.location.origin}/shared/resource-lists/${list?.hashid || id}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    });
  }

  function handleShareSubmit() {
    if (!shareEmail && !sharePhone) {
      setShareError("Please provide either an email address or a phone number.");
      return;
    }
    if (shareEmail && !validateEmail(shareEmail)) {
      setShareError("Email address is invalid.");
      return;
    }
    if (sharePhone && !validatePhoneNumber(sharePhone)) {
      setShareError("Phone number must be 10 digits.");
      return;
    }
    setShareError("");
    setShareModalOpen(false);

    const url = `${window.location.origin}/shared/resource-lists/${list?.hashid || id}`;
    resourceListService
      .share({
        id: list?.hashid || id,
        email: shareEmail,
        phone: sharePhone,
        client: shareClient,
        comments: shareComments,
        reply_to_me: shareReplyToMe,
        url,
      })
      .then(() => {
        setShareConfirmation("You should receive your resource list shortly. Thank you for using 211.");
        setShareEmail("");
        setSharePhone("");
        setShareClient("");
        setShareComments("");
        setTimeout(() => setShareConfirmation(""), 6000);
      })
      .catch(() => {
        setShareConfirmation("Your resource list couldn't be sent. Please try again.");
        setTimeout(() => setShareConfirmation(""), 6000);
      });
  }

  if (loading) {
    return (
      <div className="shopping-cart-layout">
        <div className="loading-content">
          <div className="preloader">
            <div className="dot1" />
            <div className="dot2" />
          </div>
          <p className="direct">Loading</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shopping-cart-layout">
        <div className="no-results">
          <p>Unable to load this resource list.</p>
          <a href="/">Start a new search</a>
        </div>
      </div>
    );
  }

  return (
    <div className="shopping-cart-layout">
      <MapContainer
        center={mapCenter}
        results={mapResults}
        selectedResultId={selectedItemId}
        highlightedResultId={selectedItemId}
        onMarkerClick={handleMarkerClick}
      />

      <div className="sidebar">
        <div className="sidebar-header cart-header">
          {list?.name || "Resource List"}
        </div>

        <div className="results-content">
          <button
            className="btn btn-default back-to-last-search pointer"
            onClick={() => { window.location.href = shared ? "/" : "/my211/resource-lists"; }}
          >
            <i className="ico ico-arrowright" /> Back
          </button>

          <div className="actions">
            <a
              className="share share-cart small-label pointer"
              href="#share"
              onClick={handleShareOpen}
              role="button"
            >
              <i className="ico ico-share" /> Print &amp; Share List
            </a>
          </div>

          <p className="resource-count">
            {items.length} {items.length === 1 ? "resource" : "resources"}
          </p>

          {shareConfirmation && (
            <div className="share-list-confirmation open">
              {shareConfirmation}
            </div>
          )}

          <div className="share-resource-list">
            {shareModalOpen && (
              <div
                className="modal share-modal"
                role="dialog"
                tabIndex={-1}
                style={{ display: "block", marginTop: "6em", zIndex: 9999 }}
              >
                <div className="modal-dialog" role="document">
                  <div className="modal-content">
                    <div className="modal-header">
                      <div className="actions">
                        {linkCopied ? (
                          <button className="btn copy-link-confirmation">
                            <i className="glyphicon glyphicon-check" />
                            {" "}Link copied
                          </button>
                        ) : (
                          <button className="btn copy-link-button" onClick={handleCopyLink}>
                            <i className="glyphicon glyphicon-copy" />
                            {" "}Copy link
                          </button>
                        )}
                        <button className="btn" onClick={handlePrint}>
                          <i className="glyphicon glyphicon-print" />
                          {" "}Print List
                        </button>
                      </div>

                      <h4>Share resource list</h4>
                      {shareError && <div className="share-error">{shareError}</div>}
                    </div>

                    <ul className="nav nav-tabs" role="tablist">
                      <li className={shareTab === "email" ? "active" : ""} role="presentation">
                        <a
                          role="tab"
                          href="#"
                          onClick={(e) => { e.preventDefault(); setShareTab("email"); }}
                        >
                          E-mail
                        </a>
                      </li>
                      {enableSMS && (
                        <li className={shareTab === "text" ? "active" : ""} role="presentation">
                          <a
                            role="tab"
                            href="#"
                            onClick={(e) => { e.preventDefault(); setShareTab("text"); }}
                          >
                            Text message
                          </a>
                        </li>
                      )}
                    </ul>

                    <div className="tab-content">
                      {shareTab === "email" && (
                        <div className="tab-pane active" role="tabpanel">
                          <div className="modal-body share-content">
                            <label className="label-no-transform" htmlFor="email">
                              * E-mail this information to:
                            </label>
                            <input
                              id="email"
                              className="input share-address"
                              name="email"
                              placeholder="List of comma-separated email addresses"
                              value={shareEmail}
                              onChange={(e) => setShareEmail(e.target.value)}
                            />

                            <label className="label-no-transform optional">Optional information:</label>
                            <input
                              className="input share-client"
                              name="client"
                              placeholder="Name"
                              value={shareClient}
                              onChange={(e) => setShareClient(e.target.value)}
                            />

                            <textarea
                              className="input share-comments"
                              name="comments"
                              placeholder="Comments and contact information"
                              rows={5}
                              wrap="hard"
                              value={shareComments}
                              onChange={(e) => setShareComments(e.target.value)}
                            />

                            {currentUser && (
                              <p>
                                <input
                                  id="reply-to-me"
                                  className="input"
                                  name="reply_to_me"
                                  type="checkbox"
                                  checked={shareReplyToMe}
                                  onChange={() => setShareReplyToMe(!shareReplyToMe)}
                                />
                                <label htmlFor="reply-to-me">&nbsp;Send replies to me</label>
                              </p>
                            )}

                            <button className="btn btn-primary" name="send" onClick={handleShareSubmit}>
                              Send e-mail
                            </button>
                          </div>
                        </div>
                      )}

                      {shareTab === "text" && (
                        <div className="tab-pane active" role="tabpanel">
                          <div className="modal-body share-content">
                            <label className="label-no-transform" htmlFor="phone">
                              * Text this information to:
                            </label>
                            <input
                              id="phone"
                              className="input share-phone"
                              name="phone"
                              placeholder="Mobile phone number"
                              value={sharePhone}
                              onChange={(e) => setSharePhone(e.target.value)}
                            />

                            <p style={{ padding: "1em 0" }}>
                              <button className="btn btn-primary" name="send" onClick={handleShareSubmit}>
                                Send text message
                              </button>
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="modal-footer">
                      <button className="btn btn-default" type="button" onClick={handleShareClose}>
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="results-region shopping-cart">
          {items.length === 0 ? (
            <div className="no-results" style={{ margin: "1em 1.5em" }}>
              <p>No resources in this list.</p>
              <a href="/">Start a new search to add resources</a>
            </div>
          ) : (
            <div className="search-results cart-list">
              {items.map((item) => {
                const itemId = item.program_site_id || item.id;
                return (
                  <CartItem
                    key={itemId}
                    ref={(node) => {
                      if (node) itemRefs.current[itemId] = node;
                      else delete itemRefs.current[itemId];
                    }}
                    item={item}
                    onSelect={handleSelectItem}
                    onNavigate={handleNavigateToItem}
                    onRemove={shared ? null : handleRemoveItem}
                    isSelected={selectedItemId === itemId}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResourceListDetailPage;
